import { useState } from 'react';
import { Modal, ModalHeader, ModalTitle, ModalBody } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select } from '@/components/ui/select';
import { useBlockCustomer } from '../hooks/useFraud';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
  customerName: string;
}

const durationOptions = [
  { value: '1', label: '1 Day' },
  { value: '3', label: '3 Days' },
  { value: '7', label: '7 Days' },
  { value: '30', label: '30 Days' },
];

export function BlockCustomerDialog({ isOpen, onClose, customerId, customerName }: Props) {
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState('7');
  const { mutate: blockCustomer, isPending } = useBlockCustomer();

  const handleSubmit = () => {
    if (!reason.trim() || reason.trim().length < 5) return;
    blockCustomer(
      { customerId, payload: { reason: reason.trim(), duration_days: Number(duration) as 1 | 3 | 7 | 30 } },
      {
        onSuccess: () => {
          setReason('');
          setDuration('7');
          onClose();
        },
      }
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalHeader onClose={onClose}>
        <ModalTitle>Block {customerName}</ModalTitle>
      </ModalHeader>
      <ModalBody className="space-y-4">
        <p className="text-sm text-muted-foreground">
          This will temporarily block the customer from making new bookings.
        </p>

        <div className="space-y-2">
          <Label>Duration</Label>
          <Select
            value={duration}
            onChange={setDuration}
            options={durationOptions}
            className="w-full"
          />
        </div>

        <div className="space-y-2">
          <Label>Reason <span className="text-destructive">*</span></Label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain why this customer is being blocked..."
            rows={3}
          />
          {reason.trim().length > 0 && reason.trim().length < 5 && (
            <p className="text-xs text-destructive">Reason must be at least 5 characters</p>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={isPending || reason.trim().length < 5}
          >
            {isPending ? 'Blocking...' : 'Block Customer'}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
}
